import { formatUnitPrice as formatCurrency, formatWholeDollars } from '../utils/numberFormat';
import { PageShell, DataTable } from '../components/ui/DataPrimitives';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { LoadingSpinner, Modal, Badge, colors } from '../components/SharedComponents';
import { LineChart, Line, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { API_BASE } from '../config';
import OpportunityCallout from '../components/OpportunityCallout';
import LeadtimeRealityCell from '../components/LeadtimeRealityCell';
import MaterialSalesSection from '../components/MaterialSalesSection';
import MaterialPriceOverview from '../components/MaterialPriceOverview';
import { useSalesData, salesQuery, SalesBoundary } from '../components/SalesDossierShared';
import { chartMonth, chartTimeData, chartTimeAxis, chartTicks, chartAxis, chartGrid, chartPalette, chartPrice, chartTooltipStyle } from '../components/charts/SalesChartPrimitives';
// Lane 206 (Linear rebuild, Stage 2): page-scoped light-only token layer.
import './MaterialDossier.linear.css';

function MaterialDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, opportunitiesEnabled, leadtimeUiEnabled } = useAuth();
  const [material, setMaterial] = useState(null);
  const [dossierTab,setDossierTab] = useState('suppliers');
  const [salesFilters,setSalesFilters] = useState({});
  const headlineRequest = useSalesData(`/api/materials/${encodeURIComponent(id)}/sales`,token);
  const salesRequest = useSalesData(`/api/materials/${encodeURIComponent(id)}/sales?${salesQuery(salesFilters)}`,token);
  useEffect(()=>{setDossierTab('suppliers');setSalesFilters({});},[id,token]);
  const [suppliers, setSuppliers] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceOutliers, setPriceOutliers] = useState([]);
  const [priceOutlierCount, setPriceOutlierCount] = useState(0);
  const [priceOutlierFootnoteOpen, setPriceOutlierFootnoteOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [sourcingFacts, setSourcingFacts] = useState(null);
  const fetchSequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalData, setContractModalData] = useState(null);
  const [contractModalLoading, setContractModalLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Lane 206: which Explore panel is revealed (Notes / Context). null = none.
  const [activeExplore, setActiveExplore] = useState(null);

  // Order optimization state (Feature 17)
  const [orderOptimization, setOrderOptimization] = useState(null);
  const [orderOptLoading, setOrderOptLoading] = useState(false);
  const [holdingCostPct, setHoldingCostPct] = useState(25);

  // Lead-time reality (Lane 156, Track B / B4). Double OFF-lock: no fetch
  // when OPPORTUNITIES_LEADTIME_ENABLED is off (leadtimeUiEnabled via /me);
  // the endpoint independently gates on the same env var. Server-computed
  // signal blocks keyed by EXACT supplier-name strings (MAS names + PO alias
  // names, normalized server-side) — this page only does exact-string lookup,
  // never a JS re-implementation of the engine's supplier_key normalization.
  const [leadtimeReality, setLeadtimeReality] = useState(null);
  useEffect(() => {
    if (!leadtimeUiEnabled || !token || !id) {
      setLeadtimeReality(null);
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/materials/${encodeURIComponent(id)}/leadtime-reality`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (!cancelled) setLeadtimeReality(body); })
      .catch(() => { if (!cancelled) setLeadtimeReality(null); });
    return () => { cancelled = true; };
  }, [id, token, leadtimeUiEnabled]);
  const leadtimeBlocks = (leadtimeReality && leadtimeReality.has_order_dates
    && leadtimeReality.suppliers) || null;

  // Context fields state (WS13A)
  const [contextFields, setContextFields] = useState({ strategic_notes: '', market_notes: '', supplier_notes: '' });
  const [contextSaving, setContextSaving] = useState({});
  const [contextStatus, setContextStatus] = useState({});

  useEffect(() => {
    if (id) {
      fetchAllData();
    }
    return () => { fetchSequence.current += 1; };
  }, [id, token]);

  const fetchAllData = async () => {
    const request = ++fetchSequence.current;
    setSourcingFacts(null);
    setLoading(true);
    setFetchError(null);
    try {
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [materialRes, suppliersRes, priceRes, notesRes, contractsRes, sourcingRes] = await Promise.all([
        fetch(`${API_BASE}/api/materials/${id}`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/materials/${id}/approved-suppliers`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/materials/${id}/price-history`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/materials/${id}/notes`, { headers: authHeaders }).catch(() => ({ ok: false })),
        fetch(`${API_BASE}/api/materials/${id}/contracts`, { headers: authHeaders }).catch(() => ({ ok: false })),
        fetch(`${API_BASE}/api/materials/${id}/sourcing-facts`, { headers: authHeaders }).catch(() => ({ ok: false })),
      ]);

      const [matData, suppliersData, priceData, notesData, contractsData, sourcingData] = await Promise.all(
        [materialRes, suppliersRes, priceRes, notesRes, contractsRes, sourcingRes].map(res => res.ok ? res.json() : null)
      );
      if (request !== fetchSequence.current) return;
      if (!materialRes.ok) throw new Error('Material unavailable');
      setMaterial(matData);
      setContextFields({ strategic_notes: matData.strategic_notes || '', market_notes: matData.market_notes || '', supplier_notes: matData.supplier_notes || '' });
      setSuppliers(suppliersData || []);
      setPriceHistory(Array.isArray(priceData) ? priceData : priceData?.data || []);
      setPriceOutliers(priceData?.outliers || []);
      setPriceOutlierCount(priceData?.outlier_count || 0);
      setNotes(notesData || []);
      setContracts(contractsData || []);
      setSourcingFacts(sourcingData);
      // Fetch order optimization independently (non-blocking)
      fetchOrderOptimization(holdingCostPct / 100);

    } catch (err) {
      console.error('Error fetching data:', err);
      if (request === fetchSequence.current) setFetchError('Unable to load material details. Please try again.');
    } finally {
      if (request === fetchSequence.current) setLoading(false);
    }
  };

  const fetchOrderOptimization = async (costPct) => {
    if (!token || !id) return;
    setOrderOptLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/analytics/order-optimization/${encodeURIComponent(id)}?holding_cost_pct=${costPct}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        setOrderOptimization(await res.json());
      }
    } catch (err) {
      console.log('Order optimization fetch failed:', err.message);
    } finally {
      setOrderOptLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setSavingNote(true);
    try {
      const response = await fetch(`${API_BASE}/api/materials/${id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ note_text: newNote, note_type: 'general' }),
      });

      if (response.ok) {
        setNewNote('');
        const notesRes = await fetch(`${API_BASE}/api/materials/${id}/notes`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (notesRes.ok) setNotes(await notesRes.json());
      }
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const openContractModal = async (contract) => {
    setSelectedContract(contract);
    setContractModalOpen(true);
    setContractModalLoading(true);
    setContractModalData(null);
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${contract.contract_id}/parsed`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        if (data.parsed) {
          setContractModalData(data.terms);
        }
      }
    } catch (err) {
      console.error('Error loading contract details:', err);
    } finally {
      setContractModalLoading(false);
    }
  };

  const saveContextField = async (fieldName) => {
    setContextSaving(prev => ({ ...prev, [fieldName]: true }));
    setContextStatus(prev => ({ ...prev, [fieldName]: null }));
    try {
      const res = await fetch(`${API_BASE}/api/materials/${id}/context`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ [fieldName]: contextFields[fieldName] }),
      });
      if (res.ok) {
        setContextStatus(prev => ({ ...prev, [fieldName]: 'saved' }));
        setTimeout(() => setContextStatus(prev => ({ ...prev, [fieldName]: null })), 3000);
      } else {
        setContextStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      }
    } catch {
      setContextStatus(prev => ({ ...prev, [fieldName]: 'error' }));
    } finally {
      setContextSaving(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Lane 206: toggle an Explore panel (Notes / Context) open or closed.
  const openExplore = (key) => setActiveExplore(prev => (prev === key ? null : key));

  const parseClauses = (clauses) => {
    if (!clauses) return {};
    if (typeof clauses === 'string') {
      try { return JSON.parse(clauses); } catch { return {}; }
    }
    return clauses;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilExpiry = (date) => {
    if (!date) return null;
    const expiry = new Date(date);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate PPV from price history
  const calculatePPV = () => {
    const stdCost = material?.current_standard_cost || material?.standard_cost;
    if (!priceHistory.length || !stdCost) return null;
    const recentPurchases = [...priceHistory].sort((a, b) => new Date(b.date || b.po_date) - new Date(a.date || a.po_date)).slice(0, 10);
    const totalSpend = recentPurchases.reduce((sum, p) => {
      const price = p.price || p.unit_price || 0;
      const qty = p.quantity || p.purchase_quantity || 0;
      return sum + (p.total_value || price * qty);
    }, 0);
    const totalQty = recentPurchases.reduce((sum, p) => sum + (p.quantity || p.purchase_quantity || 0), 0);
    if (!totalQty) return null;
    const avgPrice = totalSpend / totalQty;
    if (!stdCost) return null;
    const ppv = (avgPrice - stdCost) * totalQty;
    const ppvPercent = ((avgPrice - stdCost) / stdCost) * 100;
    return { ppv, ppvPercent, avgPrice };
  };

  // Calculate price trend for snapshot. The latest price is the server's shared
  // last-paid figure (Lane 1322), the same number chat and Sourcing events show;
  // the browser's own pick from the history is only a fallback.
  const serverLastPaid = material?.last_paid_unit_price != null
    ? { price: material.last_paid_unit_price, date: material.last_paid_date }
    : null;
  const getPriceTrend = () => {
    if (priceHistory.length < 2) {
      // Even with 1 purchase, show the price
      if (priceHistory.length === 1) {
        const price = serverLastPaid ? serverLastPaid.price : (priceHistory[0].price || priceHistory[0].unit_price);
        return {
          direction: 'stable',
          percentage: 0,
          currentPrice: price,
          lastDate: serverLastPaid?.date || priceHistory[0].date || priceHistory[0].po_date,
        };
      }
      return null;
    }
    // With the server's latest price, the oldest price must come from the
    // server's own last-paid order (receipt date, else PO date, then PO row),
    // or the trend can compare the latest purchase with itself.
    const byPaid = serverLastPaid && priceHistory.every((r) => typeof r.paid_rank === 'number');
    const sorted = byPaid
      ? [...priceHistory].sort((a, b) => a.paid_rank - b.paid_rank)
      : [...priceHistory].sort((a, b) => new Date(a.date || a.po_date) - new Date(b.date || b.po_date));
    const oldPrice = sorted[0].price || sorted[0].unit_price;
    const newPrice = serverLastPaid ? serverLastPaid.price : (sorted[sorted.length - 1].price || sorted[sorted.length - 1].unit_price);
    if (!oldPrice || !newPrice) return null;
    const change = ((newPrice - oldPrice) / oldPrice) * 100;
    return {
      direction: Math.abs(change) < 2 ? 'stable' : change > 0 ? 'up' : 'down',
      percentage: change,
      currentPrice: newPrice,
      lastDate: serverLastPaid?.date || sorted[sorted.length - 1].date || sorted[sorted.length - 1].po_date,
      oldestDate: (byPaid && sorted[0].paid_on) || sorted[0].date || sorted[0].po_date,
    };
  };

  const ppvData = calculatePPV();
  const priceTrend = getPriceTrend();

  if (loading) {
    return (
      <PageShell className="matd">
        <div style={{ padding: '48px' }}><LoadingSpinner text={`Loading ${id}...`} /></div>
      </PageShell>
    );
  }

  if (fetchError) {
    return (
      <PageShell className="matd">
        <div className="matd-wrap">
          <div className="matd-empty" style={{ marginTop: '40px' }}>
            <div style={{ color: 'var(--crit)', marginBottom: '16px' }}>{fetchError}</div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="matd-btn" onClick={() => navigate('/materials')}>{'←'} Back to Materials</button>
              <button className="matd-btn pri" onClick={fetchAllData}>Try again</button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!material) {
    return (
      <PageShell className="matd">
        <div className="matd-wrap">
          <div className="matd-empty" style={{ marginTop: '40px' }}>
            <div style={{ color: 'var(--crit)', fontSize: '16px', marginBottom: '8px' }}>Material not found</div>
            <p className="matd-muted" style={{ marginBottom: '16px' }}>The material "{id}" could not be found.</p>
            <button className="matd-btn" onClick={() => navigate('/materials')}>{'←'} Back to Materials</button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Derived header/figure data ----
  const annualSpend = material.annual_spend || material.total_spend;
  const trendArrow = priceTrend?.direction === 'up' ? '↗' : priceTrend?.direction === 'down' ? '↘' : '→';
  const trendCss = priceTrend?.direction === 'up' ? 'var(--up)' : priceTrend?.direction === 'down' ? 'var(--down)' : 'var(--text-3)';
  const primarySupplierName = suppliers.find(s => s.is_primary_supplier)?.supplier_name;
  const poCountMeta = material.po_count || priceHistory.length || null;
  const lastPurchaseDate = material.last_purchase || priceTrend?.lastDate;

  // Figure strip — only figures with data (parity: Key Metrics + Snapshot fields)
  const figs = [];
  if (annualSpend) {
    figs.push({
      k: 'Spend · 12mo',
      v: formatWholeDollars(annualSpend),
      m: [poCountMeta ? `${poCountMeta.toLocaleString()} POs` : null, lastPurchaseDate ? `last purchase ${formatDate(lastPurchaseDate)}` : null].filter(Boolean).join(' · '),
    });
  }
  if (priceTrend) {
    figs.push({
      k: 'Latest unit price',
      v: (
        <>
          {formatCurrency(priceTrend.currentPrice)}
          {priceTrend.percentage !== 0 && (
            <small style={{ color: trendCss }}> {trendArrow} {priceTrend.percentage > 0 ? '+' : ''}{priceTrend.percentage.toFixed(1)}%</small>
          )}
        </>
      ),
      m: priceTrend.percentage !== 0 && priceTrend.oldestDate
        ? `since ${formatDate(priceTrend.oldestDate)}`
        : (lastPurchaseDate ? `last purchase ${formatDate(lastPurchaseDate)}` : ''),
    });
  }
  figs.push({
    k: 'Approved suppliers',
    v: <span style={{ color: suppliers.length <= 1 ? 'var(--crit)' : undefined }}>{suppliers.length}</span>,
    m: primarySupplierName
      ? `${primarySupplierName}${suppliers.length <= 1 ? ' · sole source' : ''}`
      : (suppliers.length <= 1 ? 'sole source' : `${suppliers.length} approved`),
  });
  // Actual lead time measured (Lane 307, QA-2b). Display-only summary of the
  // per-supplier "Actual (measured)" detail below -- sourced ENTIRELY from the
  // leadtimeBlocks already fetched from /leadtime-reality (Lane 156); no new
  // fetch, no cross-supplier aggregate. Gated implicitly on the same
  // OPPORTUNITIES_LEADTIME_ENABLED + order-date state (leadtimeBlocks is null
  // otherwise, so no card). Prefer the primary supplier's measured figure; else
  // fall back to the most-evidenced (highest po_count) measured supplier; omit
  // if none is measured. Attributed to the supplier it came from -- this is that
  // supplier's measured lead time, not a synthesized material-wide number.
  if (leadtimeBlocks) {
    // ⭐ LANE 977 -- this summary card and the per-supplier cell below it must
    // read the SAME figure off the SAME block. They are two renderings of one
    // number, and the whole point of this lane is that a buyer never reads two.
    // `ltValue`/`ltCount` therefore resolve exactly as LeadtimeRealityCell.js
    // does: the median when the server sent one (flag ON), the 6-month mean
    // otherwise (flag OFF, every environment today).
    // ⛔ LANE 985 -- one predicate, used by all three of the resolvers below and
    // by the render. It answers "did the ONE writer send its figure?", which is
    // also "is this number already rounded?" (round_leadtime_days() in
    // backend/helpers/delivery_reliability.py rounds it and nothing else does).
    const ltHasMedian = (b) => (
      !!b && b.median_lt_days !== null && b.median_lt_days !== undefined);
    const ltValue = (b) => {
      if (!b) return null;
      if (ltHasMedian(b)) return b.median_lt_days;
      return (b.actual_avg_lt_days !== null && b.actual_avg_lt_days !== undefined)
        ? b.actual_avg_lt_days : null;
    };
    // The count must belong to the figure being shown -- the median's sample is
    // a 12-month one and differs from the 6-month mean's (2 vs 11 on the named
    // case). Pairing a figure with the other measure's count is the same class
    // of defect this lane removes.
    const ltCount = (b) => (
      ltHasMedian(b) ? (b.median_po_count || 0) : ((b && b.po_count) || 0));
    const isMeasured = (b) => ltValue(b) !== null;
    const primaryLtBlock = primarySupplierName ? leadtimeBlocks[primarySupplierName] : null;
    let ltBlock = isMeasured(primaryLtBlock) ? primaryLtBlock : null;
    let ltSupplierName = ltBlock ? primarySupplierName : null;
    if (!ltBlock) {
      Object.keys(leadtimeBlocks).forEach((name) => {
        const b = leadtimeBlocks[name];
        if (isMeasured(b) && (ltBlock === null || ltCount(b) > ltCount(ltBlock))) {
          ltBlock = b;
          ltSupplierName = name;
        }
      });
    }
    if (ltBlock) {
      const ltPoCount = ltCount(ltBlock);
      // The window the figure was measured over rides WITH it. "Actual lead
      // time measured" alone never said over what, which is how five surfaces
      // came to show five numbers and all of them looked right.
      const ltLabel = ltBlock.window_label
        ? `${ltPoCount} PO${ltPoCount !== 1 ? 's' : ''} ${ltBlock.window_label} · ${ltSupplierName}`
        : `${ltPoCount} PO${ltPoCount !== 1 ? 's' : ''} · ${ltSupplierName}`;
      // ⛔ LANE 985 -- the median is printed VERBATIM. It arrives whole from
      // round_leadtime_days(), the one place a lead-time day is rounded, so
      // this card, the per-supplier cell below it, the deck row and the chat
      // answer all print the same integer. The `Math.round` is the
      // pre-Lane-977 6-month MEAN fallback only (still a 1-dp float) and is the
      // SAME half-up rule the writer applies, not a competing one.
      figs.push({
        k: 'Actual lead time measured',
        v: (ltHasMedian(ltBlock)
          ? `~${Number(ltValue(ltBlock))} days`
          : `~${Math.round(Number(ltValue(ltBlock)))} days`),
        m: ltLabel,
      });
    }
  }
  if (ppvData) {
    figs.push({
      // Lane 135 (Lane 133 findings §7 item 2): calculatePPV averages the LAST 10
      // PURCHASES vs standard cost — a rolling figure, not year-to-date.
      k: 'Price variance · last 10 POs',
      v: <span style={{ color: ppvData.ppv > 0 ? 'var(--up)' : ppvData.ppv < 0 ? 'var(--down)' : undefined }}>{ppvData.ppvPercent > 0 ? '+' : ''}{ppvData.ppvPercent.toFixed(1)}%</span>,
      m: 'vs standard cost',
    });
  }

  return (
    <PageShell className="matd">
      {/* Breadcrumb */}
      <div className="matd-topbar">
        <div className="matd-crumb">
          <button onClick={() => navigate('/materials')}>Materials</button>
          <span className="sep">/</span>
          <span className="cur">{material.material_number}</span>
        </div>
      </div>

      <div className="matd-wrap">
        {/* Header */}
        <header className="matd-head">
          <div className="matd-idcol">
            {/* Name and material ID remain distinct in the dossier header. */}
            <div className="matd-nmeta">
              <h1 className="matd-name">{material.material_name || material.material_number} <span className="sales-header-id">{material.material_name && material.material_number}</span></h1>
              {material.status && (
                <span className={`matd-badge ${material.status === 'active' ? 'ok' : 'mut'}`}>{material.status}</span>
              )}
              {suppliers.length <= 1 && (
                <span className="matd-badge crit">Single source</span>
              )}
            </div>

            {(material.level_1 || material.level_2 || material.level_3) && (
              <div className="matd-cats">
                {['level_1', 'level_2', 'level_3'].map((k) => material[`${k}_label`] || material[k]).filter(Boolean).map((lvl, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="dot">·</span>}
                    {lvl}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </header>

        <SalesBoundary scope="matd" onRetry={salesRequest.reload}><MaterialPriceOverview request={headlineRequest}/></SalesBoundary>
        <div className="sales-dossier-tabs" role="tablist" aria-label="Material sections">{['suppliers','customers'].map(tab=><button key={tab} type="button" id={`material-tab-${tab}`} role="tab" aria-controls={`material-panel-${tab}`} aria-selected={dossierTab===tab} onClick={()=>setDossierTab(tab)}>{tab==='suppliers'?'Suppliers':'Customers'}</button>)}</div>
        <div role="tabpanel" id="material-panel-customers" aria-labelledby="material-tab-customers" hidden={dossierTab!=='customers'}><SalesBoundary scope="matd" onRetry={salesRequest.reload}><MaterialSalesSection request={salesRequest} filters={salesFilters} setFilters={setSalesFilters}/></SalesBoundary></div>
        <div role="tabpanel" id="material-panel-suppliers" aria-labelledby="material-tab-suppliers" hidden={dossierTab!=='suppliers'}>
        {/* ===== KEY FIGURES (Key Metrics row + Snapshot price/supplier/risk fields) ===== */}
        {figs.length > 0 && (
          <section className="matd-sec" style={{ marginTop: '26px' }}>
            <div className="matd-figs">
              {figs.map((f, idx) => (
                <div className="matd-fig" key={idx}>
                  <div className="k">{f.k}</div>
                  <div className="v">{f.v}</div>
                  {f.m && <div className="m">{f.m}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== PRICE & PURCHASES ===== */}
        {priceHistory.length > 0 && (
          <section className="matd-sec">
            <div className="matd-sec-h">
              <span className="matd-lbl">Price &amp; purchases</span>
              <span className="note">{priceHistory.length} purchase{priceHistory.length !== 1 ? 's' : ''}: trend, purchases and volume together</span>
            </div>

            {(() => {
              // Group price history by supplier and prepare chart data
              const validHistory = priceHistory.filter(r => (r.price || r.unit_price) > 0);
              const chartSuppliers = [...new Set(validHistory.map(r => r.supplier || r.supplier_name))].filter(Boolean);
              const standardCost = material?.current_standard_cost || material?.standard_cost;

              // Build line chart data: one row per date, one column per supplier
              const byDate = {};
              validHistory.forEach(r => {
                const dateStr = r.date || r.po_date;
                if (!dateStr) return;
                const label = new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                if (!byDate[dateStr]) byDate[dateStr] = { date: dateStr, label, _outliers: {} };
                const supplierKey = r.supplier || r.supplier_name;
                const price = r.price || r.unit_price;
                if (r.is_outlier) {
                  byDate[dateStr][supplierKey] = r.capped_price || price;
                  byDate[dateStr]._outliers[supplierKey] = price;
                } else {
                  byDate[dateStr][supplierKey] = price;
                }
              });
              const lineData = Object.values(byDate).sort((a, b) => new Date(a.date) - new Date(b.date));

              // Compute Y-axis domain from non-outlier prices only
              const nonOutlierPrices = validHistory.filter(r => !r.is_outlier).map(r => r.price || r.unit_price).filter(v => v > 0);
              if (standardCost > 0) nonOutlierPrices.push(standardCost);
              const priceYMin = nonOutlierPrices.length ? Math.min(...nonOutlierPrices) : 0;
              const priceYMax = nonOutlierPrices.length ? Math.max(...nonOutlierPrices) : 100;
              const pricePad = (priceYMax - priceYMin) * 0.1 || 1;

              // Build scatter data: one point per PO
              const scatterData = validHistory.map(r => ({
                quantity: r.quantity || r.purchase_quantity || 0,
                price: r.price || r.unit_price,
                supplier: r.supplier || r.supplier_name || '-',
                date: new Date(r.date || r.po_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                is_outlier: r.is_outlier || false,
              }));

              // Scatter axis domain from non-outlier points
              const nonOutlierScatter = scatterData.filter(d => !d.is_outlier);
              const scatterPrices = nonOutlierScatter.map(d => d.price).filter(v => v > 0);
              const scatterQtys = nonOutlierScatter.map(d => d.quantity).filter(v => v > 0);
              const scatterYMin = scatterPrices.length ? Math.min(...scatterPrices) : 0;
              const scatterYMax = scatterPrices.length ? Math.max(...scatterPrices) : 100;
              const scatterXMax = scatterQtys.length ? Math.max(...scatterQtys) : 100;
              const scatterYPad = (scatterYMax - scatterYMin) * 0.1 || 1;
              const scatterXPad = scatterXMax * 0.1 || 10;

              return (
                <>
                  {/* Chart 1: Price Over Time by Supplier (full width) */}
                  <div className="matd-panel">
                    <div className="matd-panel-h">
                      <div className="t">Price over time by supplier</div>
                    </div>
                    {lineData.length < 2 ? (
                      <div className="matd-empty">Not enough data points to show trend</div>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={chartTimeData(lineData,'date')} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid {...chartGrid} />
                            <XAxis {...chartAxis} {...chartTimeAxis} ticks={chartTicks(lineData,"date")} tickFormatter={chartMonth} />
                            <YAxis {...chartAxis} domain={[Math.max(0, priceYMin - pricePad), priceYMax + pricePad]} tickFormatter={formatChartPrice} width={65} />
                            <Tooltip content={<PriceTrendTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                            {standardCost > 0 && (
                              <ReferenceLine y={standardCost} stroke={colors.electricBlue} strokeDasharray="4 3" strokeWidth={1.5} label={{ value: 'Std Cost', position: 'right', fontSize: 10, fill: colors.electricBlue }} />
                            )}
                            {chartSuppliers.map((supplier, idx) => {
                              const color = SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length];
                              return (
                                <Line
                                  key={supplier}
                                  type="monotone"
                                  dataKey={supplier}
                                  name={supplier}
                                  stroke={color}
                                  strokeWidth={2}
                                  dot={(props) => {
                                    const row = lineData[props.index];
                                    const isOut = row?._outliers?.[supplier] != null;
                                    if (isOut) {
                                      return <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill="none" stroke="#D97706" strokeWidth={2} />;
                                    }
                                    return <circle key={props.index} cx={props.cx} cy={props.cy} r={3} fill={color} strokeWidth={2} />;
                                  }}
                                  activeDot={{ r: 5 }}
                                  connectNulls
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                        {/* Outlier footnote for line chart */}
                        {priceOutlierCount > 0 && (
                          <div className="matd-note">
                            <div className="tog" onClick={() => setPriceOutlierFootnoteOpen(!priceOutlierFootnoteOpen)}>
                              <span className="car">{priceOutlierFootnoteOpen ? '▼' : '▶'}</span>
                              {priceOutlierCount} data point{priceOutlierCount !== 1 ? 's' : ''} excluded from scale
                            </div>
                            {priceOutlierFootnoteOpen && priceOutliers.map((o, i) => (
                              <div key={i} className="outrow">{o.supplier}: {formatChartPrice(o.price)} on {o.date}</div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Chart 2: Price vs Quantity (full width) */}
                  {scatterData.length > 0 && (
                    <div className="matd-panel">
                      <div className="matd-panel-h">
                        <div className="t">Price vs quantity by supplier</div>
                        <div className="r">per PO</div>
                      </div>
                      <div className="matd-sub">Downward slope suggests volume discounts</div>
                      <ResponsiveContainer width="100%" height={240}>
                        <ScatterChart margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                          <CartesianGrid {...chartGrid} />
                          <XAxis {...chartAxis} type="number" dataKey="quantity" name="Quantity" domain={[0, scatterXMax + scatterXPad]} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v.toLocaleString()} />
                          <YAxis {...chartAxis} type="number" dataKey="price" name="Price" domain={[Math.max(0, scatterYMin - scatterYPad), scatterYMax + scatterYPad]} tickFormatter={formatChartPrice} width={65} />
                          <Tooltip content={<ScatterTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          {chartSuppliers.map((supplier, idx) => (
                            <Scatter
                              key={supplier}
                              name={supplier}
                              data={scatterData.filter(d => d.supplier === supplier)}
                              fill={SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length]}
                              opacity={0.8}
                            />
                          ))}
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Recent Purchases table */}
            {/* R3-MATDOSSIER (Lane 337): per-PO order->receipt lead-time column.
                Gated like every lead-time surface (Lane 307 precedent):
                OPPORTUNITIES_LEADTIME_ENABLED via /me (leadtimeUiEnabled) AND
                real per-PO data present -- a tenant with no order dates sees NO
                column. Per-row: the backend's additive lead_time_days echo
                ((receipt - order) days, null unless both dates exist and the
                span is non-negative); a row without it renders an em dash,
                never a fabricated number. */}
            {(() => {
              const showPurchaseLeadtime = leadtimeUiEnabled
                && priceHistory.some((r) => typeof r.lead_time_days === 'number' && r.lead_time_days >= 0);
              return (
                <>
                  <div className="matd-sec-h" style={{ marginTop: '22px' }}>
                    <span className="matd-lbl">Recent purchases</span>
                  </div>
                  <div className="matd-scroll">
                    <DataTable className="matd-tbl">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Supplier</th>
                          <th className="right">Price</th>
                          <th className="right">Qty</th>
                          {showPurchaseLeadtime && <th className="right">Lead time (days)</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {[...priceHistory].sort((a, b) => new Date(b.date || b.po_date) - new Date(a.date || a.po_date)).map((record, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(record.date || record.po_date)}</td>
                            <td>{record.supplier || record.supplier_name || '-'}</td>
                            <td className="num">{formatCurrency(record.price || record.unit_price)}</td>
                            <td className="num">{(record.quantity || record.purchase_quantity)?.toLocaleString() || '-'}</td>
                            {showPurchaseLeadtime && (
                              <td className="num">
                                {typeof record.lead_time_days === 'number' && record.lead_time_days >= 0
                                  ? record.lead_time_days.toLocaleString()
                                  : 'Not available'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </div>
                </>
              );
            })()}
          </section>
        )}



        {/* ===== APPROVED SUPPLIERS ===== */}
        {suppliers.length > 0 && (
          <section className="matd-sec">
            <div className="matd-sec-h">
              <span className="matd-lbl">Approved suppliers</span>
              <span className="note">{suppliers.length} approved{suppliers.length <= 1 ? ', which drives the single-source flag' : ''}</span>
            </div>
            <DataTable className="matd-tbl">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th className="center">Status</th>
                  <th className="center">Lead time</th>
                  {/* Lane 156 (Track B / B4): the reality check beside the quoted
                      number. Column exists ONLY when the tenant has real order
                      dates — key-presence-conditional, so flag-OFF (and XMEK's
                      no-order-date state) renders the exact table shipping today. */}
                  {leadtimeBlocks && <th className="center">Actual (measured)</th>}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier, idx) => (
                  <tr key={idx}>
                    <td>
                      <span style={{ fontWeight: supplier.is_primary_supplier ? 600 : 400 }}>{supplier.supplier_name}</span>
                      {supplier.is_primary_supplier && (
                        <span className="matd-pill pri" style={{ marginLeft: '8px' }}>Primary</span>
                      )}
                    </td>
                    <td className="center">
                      <span className={`matd-pill ${supplier.approval_status === 'approved' ? 'good' : 'mut'}`}>{supplier.approval_status || 'Pending'}</span>
                    </td>
                    <td className="ui-numeric">{supplier.lead_time_days || '-'} days</td>
                    {leadtimeBlocks && (
                      <td className="center"><LeadtimeRealityCell block={leadtimeBlocks[supplier.supplier_name]} /></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {/* Honest note (Lane 156): flag ON but the PO feed carries no real
                order date (XMEK today — po_date IS the receipt date). One note
                per page; never a fabricated lead time. */}
            {leadtimeReality && leadtimeReality.leadtime_enabled
              && leadtimeReality.has_order_dates === false && (
              <div data-testid="leadtime-no-order-dates-note" className="matd-foot">
                Lead times shown are the quoted numbers. Actual order-to-receipt lead times will appear here once your PO history carries a real order date.
              </div>
            )}
          </section>
        )}

        {/* ===== SAVINGS OPPORTUNITIES / ORDER OPTIMIZATION (flag-gated conditional swap) =====
            When OPPORTUNITIES_ENABLED, render the Opportunities callout; else the legacy
            Order Optimization section (Feature 17). NOT a deletion — the flag-OFF branch keeps
            Order Optimization + its holdingCostPct control intact until the flip, so nothing is
            lost on PROD before OPPORTUNITIES_ENABLED is turned on. */}
        {opportunitiesEnabled ? (
          <section className="matd-sec">
            <OpportunityCallout materialNumber={id} />
          </section>
        ) : (orderOptimization && orderOptimization.has_data && (
          <section className="matd-sec">
            <div className="matd-sec-h">
              <span className="matd-lbl">Order optimization</span>
              <span className="note">
                {orderOptimization.summary.suppliers_with_discount > 0
                  ? `${orderOptimization.summary.suppliers_with_discount} of ${orderOptimization.summary.total_suppliers} suppliers show volume discounts`
                  : `${orderOptimization.summary.total_suppliers} suppliers analyzed`}
              </span>
            </div>

            {/* Summary */}
            {orderOptimization.summary.suppliers_with_discount > 0 ? (
              <div className="matd-callout good">
                <b>Potential annual savings: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(orderOptimization.summary.total_potential_savings)}</b>
                <span className="matd-muted" style={{ marginLeft: '8px', fontSize: '12px' }}>
                  from consolidating orders with {orderOptimization.summary.suppliers_with_discount} supplier{orderOptimization.summary.suppliers_with_discount !== 1 ? 's' : ''}
                </span>
              </div>
            ) : (
              <div className="matd-callout">
                No volume discount patterns detected across {orderOptimization.summary.total_suppliers} supplier{orderOptimization.summary.total_suppliers !== 1 ? 's' : ''}
              </div>
            )}

            {/* Holding cost input */}
            <div className="matd-ooctl">
              <span>Holding cost assumption:</span>
              <input
                className="matd-input qty"
                type="number"
                value={holdingCostPct}
                onChange={e => setHoldingCostPct(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
              />
              <span>% annual</span>
              <button className="matd-btn sm" onClick={() => fetchOrderOptimization(holdingCostPct / 100)} disabled={orderOptLoading}>
                {orderOptLoading ? 'Calculating...' : 'Recalculate'}
              </button>
            </div>

            {/* Per-supplier results */}
            {orderOptimization.results.map((result, idx) => (
              <div key={idx} className={`matd-oorow${result.status === 'Volume discount detected' ? ' on' : ''}`}>
                <div className="hd">
                  <div>
                    <span className="snm" onClick={() => navigate(`/suppliers/${encodeURIComponent(result.supplier_name)}`)}>{result.supplier_name}</span>
                    <span className="matd-muted" style={{ fontSize: '12px', marginLeft: '8px' }}>
                      {result.order_count} orders{result.dominant_uom ? ` (${result.dominant_uom})` : ''}
                    </span>
                  </div>
                  {result.status !== 'Volume discount detected' && (
                    <span className="matd-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>{result.status}</span>
                  )}
                </div>

                {/* Lane 156 (Track B / B4): lead-time reality beside the
                    consolidation math — a consolidation read should never be
                    blind to delivery reality. Renders ONLY when the engine
                    classified this supplier's pair (exact-name lookup into the
                    server-computed blocks; chip-less/unmeasured rows add nothing). */}
                {(() => {
                  const lt = leadtimeBlocks && leadtimeBlocks[result.supplier_name];
                  if (!lt || !lt.chip) return null;
                  const LT_CSS = { aligned: 'var(--good)', padded: 'var(--warn)', understated: 'var(--crit)' };
                  return (
                    <div data-testid="orderopt-leadtime-line" title={lt.reason || ''} style={{ fontSize: '12px', color: LT_CSS[lt.direction] || 'var(--text-3)', marginTop: '4px' }}>
                      Lead time: shown {lt.displayed_lt_days != null ? `${Math.round(lt.displayed_lt_days)}d` : 'Not available'}
                      {' · '}actual ~{Math.round(lt.actual_avg_lt_days)}d
                      {' '}({lt.po_count} receipt{lt.po_count !== 1 ? 's' : ''}): {lt.direction}
                    </div>
                  );
                })()}

                {result.status === 'Volume discount detected' && (
                  <div className="matd-oogrid">
                    <div><div className="k">Current Avg</div><div className="v">{formatChartPrice(result.avg_price)}/unit</div></div>
                    <div><div className="k">Consolidated</div><div className="v" style={{ color: 'var(--good)' }}>{formatChartPrice(result.consolidated_price)}/unit</div></div>
                    <div>
                      <div className="k">Net Savings</div>
                      <div className="v" style={{ color: result.net_savings > 0 ? 'var(--good)' : 'var(--crit)' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(result.net_savings)}
                        <span style={{ fontWeight: 400, marginLeft: '4px' }}>({result.savings_pct}%)</span>
                      </div>
                    </div>
                    <div><div className="k">Fit</div><div className="v" style={{ fontFamily: 'inherit' }}>R² = {result.r_squared?.toFixed(2)}</div></div>
                  </div>
                )}
              </div>
            ))}
          </section>
        ))}

        <section aria-label="Supplier facts">
          <div className="matd-sec-h"><span className="matd-lbl">Supplier facts</span></div>
          {sourcingFacts ? <>
            <div className="matd-kv-row">
              <div className="matd-kv"><span className="k">Approved suppliers</span><span className="v">{sourcingFacts.approved_supplier_count}</span></div>
              <div className="matd-kv"><span className="k">Primary supplier</span><span className="v">{sourcingFacts.primary_supplier || 'None designated'}</span></div>
              <div className="matd-kv"><span className="k">Spend concentration</span><span className="v">{sourcingFacts.concentration_percent == null ? 'Not available' : `${sourcingFacts.concentration_percent}%`}</span></div>
            </div>
            <div className="matd-foot">{sourcingFacts.concentration_basis}{sourcingFacts.top_supplier ? ` Largest supplier: ${sourcingFacts.top_supplier}.` : ''}</div>
          </> : <div className="matd-foot" role="status">Supplier facts could not be loaded.</div>}
        </section>

        {/* ===== CONTRACTS + PPV (two-col) ===== */}
        <div className="matd-cols">
          {/* Contracts */}
          <section>
            <div className="matd-sec-h">
              <span className="matd-lbl">Contracts</span>
              <span className="note">{contracts.length === 0 ? '0 on file' : `${contracts.length} active`}</span>
            </div>
            {contracts.length === 0 ? (
              <div className="matd-foot" style={{ marginTop: '2px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                No contracts cover this material. All trailing spend is spot-bought. When a contract exists, cards render here with expiry and AI-parsed terms, opening the contract detail.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {contracts.map((contract, idx) => {
                  const daysUntil = getDaysUntilExpiry(contract.end_date || contract.expiration_date);
                  const isExpiringSoon = daysUntil !== null && daysUntil <= 90 && daysUntil > 0;
                  const isExpired = daysUntil !== null && daysUntil <= 0;
                  const hasParsed = contract.parse_status === 'completed' || contract.parse_status === 'reviewed';

                  const highlights = [];
                  if (hasParsed) {
                    if (contract.parsed_auto_renewal != null) {
                      let hl = contract.parsed_auto_renewal ? 'Auto-renewal: Yes' : 'Auto-renewal: No';
                      if (contract.parsed_notice_days) hl += `, ${contract.parsed_notice_days}-day notice`;
                      highlights.push(hl);
                    }
                    if (contract.parsed_escalation_cap) {
                      highlights.push(`Escalation cap: ${contract.parsed_escalation_cap}%`);
                    } else if (contract.parsed_pricing_mechanism) {
                      highlights.push(contract.parsed_pricing_mechanism);
                    }
                  }

                  return (
                    <div key={idx} className="matd-ccard" tabIndex={0} onClick={() => openContractModal(contract)}>
                      <div className="top">
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '12px' }}>
                          <span className="cnum">{contract.contract_number || `Contract #${idx + 1}`}</span>
                          {contract.contract_name && <span className="cname">{contract.contract_name}</span>}
                        </div>
                        {isExpiringSoon && <span className="matd-pill warn">Expires in {daysUntil}d</span>}
                        {isExpired && <span className="matd-pill crit">Expired</span>}
                        {!isExpiringSoon && !isExpired && daysUntil && <span className="matd-pill good">Active</span>}
                      </div>
                      <div className="meta">
                        <span>{contract.supplier_name}</span>
                        <span>Expires: {formatDate(contract.end_date || contract.expiration_date)}</span>
                        {contract.contracted_price && <span>Price: {formatCurrency(contract.contracted_price)}</span>}
                        {hasParsed && contract.parsed_payment_terms && <span>{contract.parsed_payment_terms}</span>}
                      </div>
                      {highlights.length > 0 && <div className="hl">{highlights.join(' · ')}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* PPV Analysis — renders only when standard costs exist */}
          {ppvData && (
            <section>
              <div className="matd-sec-h">
                <span className="matd-lbl">PPV analysis</span>
                <span className="note">{ppvData.ppvPercent > 0 ? '+' : ''}{ppvData.ppvPercent.toFixed(1)}% vs standard cost</span>
              </div>
              <div className="matd-kv"><span className="k">Avg purchase price</span><span className="v matd-mono">{formatCurrency(ppvData.avgPrice)}</span></div>
              <div className="matd-kv"><span className="k">Standard cost</span><span className="v matd-mono">{formatCurrency(material.current_standard_cost || material.standard_cost)}</span></div>
              <div className="matd-kv" style={{ background: 'transparent', borderTop: '1px solid var(--hair)', borderRadius: 0, paddingTop: '10px' }}>
                <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>PPV variance</span>
                <span className="v" style={{ color: ppvData.ppv > 0 ? 'var(--up)' : 'var(--down)' }}>{ppvData.ppvPercent > 0 ? '+' : ''}{ppvData.ppvPercent.toFixed(1)}%</span>
              </div>
            </section>
          )}
        </div>

        {/* ===== EXPLORE (Notes / Context) ===== */}
        <section className="matd-sec">
          <div className="matd-sec-h"><span className="matd-lbl">Explore</span></div>
          <div className="matd-schips">
            <button className={`matd-schip${activeExplore === 'notes' ? ' on' : ''}`} onClick={() => openExplore('notes')}>
              Notes {notes.length > 0 && <span className="g">{notes.length}</span>}
            </button>
            <button className={`matd-schip${activeExplore === 'context' ? ' on' : ''}`} onClick={() => openExplore('context')}>
              Context <span className="g">3 fields</span>
            </button>
          </div>

          {/* Notes reveal */}
          {activeExplore === 'notes' && (
            <div className="matd-reveal">
              <div className="matd-noteadd">
                <input
                  className="matd-input grow"
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                  placeholder="Add a quick note..."
                />
                <button className="matd-btn pri" onClick={handleAddNote} disabled={!newNote.trim() || savingNote}>
                  {savingNote ? '...' : 'Add'}
                </button>
              </div>
              {notes.length === 0 ? (
                <div className="matd-empty">No notes yet</div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {notes.slice(0, 10).map((note, idx) => (
                    <div key={note.note_id || idx} className="matd-noteitem">
                      <div>{note.text}</div>
                      <div className="meta">{note.created_by || 'System'} {note.created_date ? `· ${formatDate(note.created_date)}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
              {notes.length > 10 && <div className="matd-empty">+{notes.length - 10} more notes</div>}
            </div>
          )}

          {/* Context reveal (WS13A) */}
          {activeExplore === 'context' && (
            <div className="matd-reveal">
              <div className="context-section">
                {[
                  { key: 'strategic_notes', label: 'Strategic Notes', placeholder: 'Sourcing strategy, dual-source plans, qualification pipeline...' },
                  { key: 'market_notes', label: 'Market Notes', placeholder: 'Commodity trends, price drivers, lead time outlook...' },
                  { key: 'supplier_notes', label: 'Supplier Notes', placeholder: 'Key supplier dynamics, negotiation history, relationship context...' },
                ].map(field => (
                  <div key={field.key} className="context-field">
                    <label className="context-field-label">{field.label}</label>
                    <textarea
                      className="context-field-textarea"
                      value={contextFields[field.key]}
                      onChange={e => setContextFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      rows={3}
                    />
                    <div className="context-field-actions">
                      <button className="matd-btn sm" onClick={() => saveContextField(field.key)} disabled={contextSaving[field.key]}>
                        {contextSaving[field.key] ? 'Saving...' : 'Save'}
                      </button>
                      {contextStatus[field.key] === 'saved' && (
                        <span className="context-field-status context-field-status--success">Saved</span>
                      )}
                      {contextStatus[field.key] === 'error' && (
                        <span className="context-field-status context-field-status--error">Error saving</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        </div>
      </div>

      {/* Contract Detail Modal */}
      {contractModalOpen && (
        <Modal
          isOpen={contractModalOpen}
          onClose={() => { setContractModalOpen(false); setContractModalData(null); setSelectedContract(null); }}
          title={`Contract: ${selectedContract?.contract_number || ''}`}
          size="medium"
        >
          {contractModalLoading ? (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <LoadingSpinner text="Loading contract details..." />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Basic Info — always available from the contract list data */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>Supplier</div>
                  <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)' }}>{selectedContract?.supplier_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>Contract Number</div>
                  <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)' }}>{selectedContract?.contract_number || '-'}</div>
                </div>
                {selectedContract?.contract_name && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>Contract Name</div>
                    <div style={{ fontSize: 'var(--font-size-base)' }}>{selectedContract.contract_name}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>Expiration Date</div>
                  <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)', color: (() => {
                    const days = getDaysUntilExpiry(selectedContract?.end_date || selectedContract?.expiration_date);
                    if (days !== null && days <= 0) return colors.danger;
                    if (days !== null && days <= 90) return colors.warning;
                    return colors.darkText;
                  })() }}>
                    {formatDate(selectedContract?.end_date || selectedContract?.expiration_date)}
                    {(() => {
                      const days = getDaysUntilExpiry(selectedContract?.end_date || selectedContract?.expiration_date);
                      if (days !== null && days > 0) return ` (${days} days)`;
                      if (days !== null && days <= 0) return ' (Expired)';
                      return '';
                    })()}
                  </div>
                </div>
                {selectedContract?.contracted_price && (
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>Contracted Price</div>
                    <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)', fontFamily: 'monospace' }}>{formatCurrency(selectedContract.contracted_price)}</div>
                  </div>
                )}
              </div>

              {/* Parsed Terms — only if AI has parsed this contract */}
              {contractModalData ? (
                <>
                  {/* Commercial Terms */}
                  <div style={{ borderTop: `1px solid ${colors.mediumGray}`, paddingTop: '16px' }}>
                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '12px' }}>Commercial Terms</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      {contractModalData.pricing_mechanism && (
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Pricing</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.pricing_mechanism}</div>
                        </div>
                      )}
                      {contractModalData.payment_terms && (
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Payment</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.payment_terms}</div>
                        </div>
                      )}
                      {contractModalData.price_escalation_cap && (
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Escalation Cap</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.price_escalation_cap}%</div>
                        </div>
                      )}
                      {contractModalData.currency && (
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Currency</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.currency}</div>
                        </div>
                      )}
                      {contractModalData.incoterms && (
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Incoterms</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.incoterms}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Renewal Terms */}
                  {(contractModalData.auto_renewal != null || contractModalData.renewal_notice_days || contractModalData.renewal_term_months) && (
                    <div style={{ borderTop: `1px solid ${colors.mediumGray}`, paddingTop: '16px' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '12px' }}>Renewal Terms</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        {contractModalData.auto_renewal != null && (
                          <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Auto-Renewal</div>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.auto_renewal ? 'Yes' : 'No'}</div>
                          </div>
                        )}
                        {contractModalData.renewal_term_months && (
                          <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Renewal Term</div>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.renewal_term_months} months</div>
                          </div>
                        )}
                        {contractModalData.renewal_notice_days && (
                          <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Notice Period</div>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>{contractModalData.renewal_notice_days} days</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Clauses */}
                  {contractModalData.clauses && (() => {
                    const clauses = parseClauses(contractModalData.clauses);
                    const clauseEntries = Object.entries(clauses).filter(([, v]) => v && (v.summary || v.is_exclusive != null));
                    if (clauseEntries.length === 0) return null;
                    return (
                      <div style={{ borderTop: `1px solid ${colors.mediumGray}`, paddingTop: '16px' }}>
                        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '12px' }}>Key Clauses</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {clauseEntries.map(([key, clause]) => (
                            <div key={key} style={{ padding: '8px 12px', backgroundColor: colors.lightGray, borderRadius: '6px' }}>
                              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, textTransform: 'capitalize', marginBottom: '2px' }}>
                                {key.replace(/_/g, ' ')}
                                {clause.is_exclusive != null && (
                                  <Badge variant={clause.is_exclusive ? 'danger' : 'success'} size="small" style={{ marginLeft: '8px' }}>
                                    {clause.is_exclusive ? 'Exclusive' : 'Non-exclusive'}
                                  </Badge>
                                )}
                              </div>
                              {clause.summary && (
                                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.secondaryText, lineHeight: '1.4' }}>{clause.summary}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)', borderTop: `1px solid ${colors.mediumGray}` }}>
                  No AI-parsed terms available for this contract.
                </div>
              )}

              {/* Close button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px', borderTop: `1px solid ${colors.mediumGray}` }}>
                <button className="matd-btn" onClick={() => { setContractModalOpen(false); setContractModalData(null); setSelectedContract(null); }}>Close</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </PageShell>
  );
}

// ===== Helper Components =====

// Color palette for multi-supplier charts
const SUPPLIER_COLORS = chartPalette;

const formatChartPrice = chartPrice;

function PriceTrendTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={chartTooltipStyle}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#111827' }}>{typeof label==='number'?chartMonth(label):label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color, display: 'inline-block' }} />
          <span style={{ color: '#6B7280' }}>{entry.name}:</span>
          <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{formatChartPrice(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div style={chartTooltipStyle}>
      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#111827' }}>{data.supplier}</div>
      <div style={{ color: '#6B7280' }}>Price: <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{formatChartPrice(data.price)}</span></div>
      <div style={{ color: '#6B7280' }}>Qty: <span style={{ fontWeight: 500 }}>{data.quantity?.toLocaleString()}</span></div>
      <div style={{ color: '#6B7280' }}>Date: <span style={{ fontWeight: 500 }}>{data.date}</span></div>
    </div>
  );
}

export default MaterialDossier;
